import type {
  IPage,
  ComponentRef,
} from '@wix/public-editor-platform-interfaces';
import type { AppUnifiedComponentData } from '@wix/document-services-types';
import type { PlatformPrivateServices } from '../../PlatformPrivateServices';
import type { EditorPointer } from '@wix/editor-document-logic-api';

const _pageKeys = new WeakMap<IPage, string>();

export class PageHandle implements IPage {
  protected readonly ps: PlatformPrivateServices;
  #editorPointer: EditorPointer<ComponentRef>;

  constructor(
    ps: PlatformPrivateServices,
    editorPointer: EditorPointer<ComponentRef>,
  ) {
    this.ps = ps;
    this.#editorPointer = editorPointer;
    _pageKeys.set(this, editorPointer.asUniqueKey);
  }

  protected getRef() {
    const ref =
      this.ps.platformPackagesBridge.EditorPointerAPI?.getDocumentPointer(
        this.#editorPointer,
      );
    if (!ref) {
      throw new Error('EditorPointerAPI is not available');
    }
    return ref;
  }

  async getExtensionId(): Promise<string | undefined> {
    const { tpaPageId, appDefinitionId } = this.ps.ds.pages.data.get(
      this.getRef().id,
    );
    if (!tpaPageId || !appDefinitionId) {
      return undefined;
    }
    // TODO(EP-8485): consolidate with getPageFromAppData in editor-platform-host-integration componentSettings.ts
    const appData = this.ps.ds.platform.getAppDataByAppDefId(appDefinitionId);
    return appData?.components?.find(
      (c) => (c.data as AppUnifiedComponentData)?.base?.id === tpaPageId,
    )?.componentId;
  }

  async getTitle(): Promise<string> {
    return this.ps.ds.pages.data.get(this.getRef().id).title;
  }

  async updateTitle(title: string): Promise<void> {
    this.ps.ds.pages.data.update(this.getRef().id, { title });
    await this.ps.ds.waitForChangesAppliedAsync();
  }

  async getSlug(): Promise<string> {
    return this.ps.ds.pages.data.get(this.getRef().id).pageUriSEO;
  }

  async updateSlug(slug: string): Promise<void> {
    this.ps.ds.pages.data.update(this.getRef().id, { pageUriSEO: slug });
    await this.ps.ds.waitForChangesAppliedAsync();
  }

  async getPageId(): Promise<string | undefined> {
    return this.ps.ds.pages.data.get(this.getRef().id).tpaPageId;
  }

  equals(other: IPage): boolean {
    return _pageKeys.get(this) === _pageKeys.get(other);
  }

  asIPage(): IPage {
    const iPage: IPage = {
      getExtensionId: () => this.getExtensionId(),
      getTitle: () => this.getTitle(),
      updateTitle: (title) => this.updateTitle(title),
      getSlug: () => this.getSlug(),
      updateSlug: (slug) => this.updateSlug(slug),
      getPageId: () => this.getPageId(),
      equals: (other: IPage) => this.equals(other),
    };
    _pageKeys.set(iPage, this.#editorPointer.asUniqueKey);
    return iPage;
  }
}
